import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import {
  cancelarTransferenciaEstoque,
  concluirTransferenciaEstoque,
  createTransferenciaEstoque,
  getAllProducts,
  getConfiguracao,
  getLocaisEstoque,
  getTransferenciaEstoque,
  getTransferenciaRomaneioPdf,
  separarTransferenciaEstoque,
  updateTransferenciaEstoque,
} from '../services/api';
import { getAuthPermissoes } from '../services/authStorage';
import { useEstabelecimento } from '../context/EstabelecimentoContext';
import PageHeader from './common/PageHeader';
import { getApiErrorMessage } from '../utils/apiError';
import {
  podeTransferencia,
  statusTransferencia,
} from '../utils/transferenciaEstoque';
import { formatDateTimeLocale } from '../utils/datetimeLocal';
import {
  PRODUCT_BARCODE_HINT,
  processarBuscaProdutoEnter,
  termoBuscaApi,
} from '../utils/productSearch';

const emptyItem = () => ({
  key: `${Date.now()}-${Math.random()}`,
  produto: null,
  qtdSolicitada: '1',
  custoUnitario: '',
});

function parseDecimal(value) {
  if (value === '' || value == null) return 0;
  const n = parseFloat(String(value).replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
}

function statusKey(status) {
  const st = statusTransferencia(status);
  return st.key;
}

export default function FormTransferenciaEstoque({ isCreate = false }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const permissions = useMemo(() => getAuthPermissoes(), []);
  const { estabelecimentos, estabelecimentoId } = useEstabelecimento();

  const creating = isCreate || !id;
  const [loading, setLoading] = useState(!creating);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [dialogError, setDialogError] = useState('');
  const [permitirEstoqueNegativo, setPermitirEstoqueNegativo] = useState(false);
  const [dlgConfirmNegativo, setDlgConfirmNegativo] = useState(null);

  const [detalhe, setDetalhe] = useState(null);
  const [origemId, setOrigemId] = useState('');
  const [destinoId, setDestinoId] = useState('');
  const [localOrigemId, setLocalOrigemId] = useState('');
  const [localDestinoId, setLocalDestinoId] = useState('');
  const [locaisOrigem, setLocaisOrigem] = useState([]);
  const [locaisDestino, setLocaisDestino] = useState([]);
  const [observacao, setObservacao] = useState('');
  const [itens, setItens] = useState([emptyItem()]);
  const [produtoOptions, setProdutoOptions] = useState([]);
  const [codigoBip, setCodigoBip] = useState('');
  const [bipando, setBipando] = useState(false);
  const bipInputRef = useRef(null);

  const [dlgSeparar, setDlgSeparar] = useState(false);
  const [dlgConcluir, setDlgConcluir] = useState(false);
  const [dlgCancelar, setDlgCancelar] = useState(false);
  const [separarItens, setSepararItens] = useState([]);
  const [concluirItens, setConcluirItens] = useState([]);
  const [motivoCancelamento, setMotivoCancelamento] = useState('');
  const [processando, setProcessando] = useState(false);
  const [baixandoPdf, setBaixandoPdf] = useState(false);

  const statusAtual = detalhe?.status;
  const statusNome = statusKey(statusAtual);
  const editavel = creating || statusNome === 'Criado';

  const filiais = useMemo(
    () => (estabelecimentos || []).map((e) => ({
      id: Number(e.id),
      nome: e.nomeFantasia || e.razaoSocial || `Filial ${e.id}`,
    })),
    [estabelecimentos],
  );

  const destinosDisponiveis = filiais;

  const carregarDetalhe = useCallback(async () => {
    if (creating) return;
    setLoading(true);
    setError('');
    try {
      const { data } = await getTransferenciaEstoque(id);
      setDetalhe(data);
      setOrigemId(String(data.estabelecimentoOrigemId));
      setDestinoId(String(data.estabelecimentoDestinoId));
      setLocalOrigemId(data.localEstoqueOrigemId ? String(data.localEstoqueOrigemId) : '');
      setLocalDestinoId(data.localEstoqueDestinoId ? String(data.localEstoqueDestinoId) : '');
      setObservacao(data.observacao || '');
      setItens(
        (data.itens || []).map((item) => ({
          key: `db-${item.id}`,
          id: item.id,
          produto: {
            id: item.produtoId,
            descricao: item.produtoDescricao,
            codigo: item.produtoCodigo,
          },
          qtdSolicitada: String(item.qtdSolicitada),
          custoUnitario: item.custoUnitario != null ? String(item.custoUnitario) : '',
        })),
      );
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível carregar a transferência.'));
    } finally {
      setLoading(false);
    }
  }, [creating, id]);

  useEffect(() => {
    carregarDetalhe();
  }, [carregarDetalhe]);

  useEffect(() => {
    if (!creating || origemId) return;
    const padrao = Number(estabelecimentoId) || filiais[0]?.id;
    if (padrao) setOrigemId(String(padrao));
  }, [creating, estabelecimentoId, filiais, origemId]);

  useEffect(() => {
    if (!creating || destinoId || !origemId) return;
    setDestinoId(origemId);
  }, [creating, destinoId, origemId]);

  useEffect(() => {
    if (!origemId) {
      setLocaisOrigem([]);
      return undefined;
    }
    let ativo = true;
    getLocaisEstoque(origemId)
      .then(({ data }) => {
        if (!ativo) return;
        const lista = (Array.isArray(data) ? data : []).filter((l) => l.ativo !== false);
        setLocaisOrigem(lista);
        setLocalOrigemId((atual) => {
          if (lista.some((l) => String(l.id) === String(atual))) return String(atual);
          const principal = lista.find((l) => l.principal) || lista[0];
          return principal ? String(principal.id) : '';
        });
      })
      .catch(() => {
        if (ativo) setLocaisOrigem([]);
      });
    return () => { ativo = false; };
  }, [origemId]);

  useEffect(() => {
    if (!destinoId) {
      setLocaisDestino([]);
      return undefined;
    }
    let ativo = true;
    getLocaisEstoque(destinoId)
      .then(({ data }) => {
        if (!ativo) return;
        const lista = (Array.isArray(data) ? data : []).filter((l) => l.ativo !== false);
        setLocaisDestino(lista);
        setLocalDestinoId((atual) => {
          const mesmaFilial = Number(origemId) === Number(destinoId);
          const atualOk = lista.some((l) => String(l.id) === String(atual));
          if (atualOk && !(mesmaFilial && String(atual) === String(localOrigemId) && lista.length > 1)) {
            return String(atual);
          }
          const outro = lista.find((l) => String(l.id) !== String(localOrigemId));
          const principal = lista.find((l) => l.principal) || lista[0];
          const escolhido = mesmaFilial && outro ? outro : principal;
          return escolhido ? String(escolhido.id) : '';
        });
      })
      .catch(() => {
        if (ativo) setLocaisDestino([]);
      });
    return () => { ativo = false; };
  }, [destinoId, origemId, localOrigemId]);

  useEffect(() => {
    const estabCfg = Number(detalhe?.estabelecimentoOrigemId)
      || Number(estabelecimentoId)
      || filiais[0]?.id;
    if (!estabCfg) return;
    getConfiguracao(estabCfg)
      .then(({ data }) => {
        setPermitirEstoqueNegativo(Boolean(data?.permitirEstoqueNegativoGerencial));
      })
      .catch(() => setPermitirEstoqueNegativo(false));
  }, [detalhe?.estabelecimentoOrigemId, estabelecimentoId, filiais]);

  const buscarProdutos = async (termo) => {
    try {
      const { data } = await getAllProducts({
        indice: 1,
        qtde: 20,
        termo: termo || '',
        ativo: 'true',
        estabelecimentoId: Number(origemId) || undefined,
      });
      setProdutoOptions(data?.items || []);
    } catch {
      setProdutoOptions([]);
    }
  };

  const adicionarProdutoRomaneio = (produto, qtd = 1) => {
    if (!produto?.id) return;
    setItens((prev) => {
      const idx = prev.findIndex((i) => Number(i.produto?.id) === Number(produto.id));
      if (idx >= 0) {
        const next = [...prev];
        const atual = parseDecimal(next[idx].qtdSolicitada);
        next[idx] = {
          ...next[idx],
          qtdSolicitada: String(atual + qtd),
        };
        return next;
      }
      const vazio = prev.findIndex((i) => !i.produto?.id);
      const novo = {
        key: `${Date.now()}-${Math.random()}`,
        produto,
        qtdSolicitada: String(qtd),
        custoUnitario: '',
      };
      if (vazio >= 0) {
        const next = [...prev];
        next[vazio] = { ...next[vazio], ...novo, key: next[vazio].key };
        return next;
      }
      return [...prev, novo];
    });
  };

  const onEnterCodigoBip = async () => {
    if (!termoBuscaApi(codigoBip)) return;
    if (!origemId) {
      setError('Selecione a filial de origem antes de bipar produtos.');
      return;
    }
    setBipando(true);
    setError('');
    try {
      const resultado = await processarBuscaProdutoEnter({
        termo: codigoBip,
        estabelecimentoId: origemId,
        ativo: 'true',
        qtde: 20,
      });
      if (resultado.tipo === 'adicionar') {
        adicionarProdutoRomaneio(resultado.produto, resultado.quantidade);
        setCodigoBip('');
        requestAnimationFrame(() => bipInputRef.current?.focus());
        return;
      }
      if (resultado.tipo === 'nao_encontrado') {
        setError(resultado.mensagem);
        return;
      }
      if (resultado.tipo === 'lista') {
        setProdutoOptions(resultado.produtos);
        setError('Vários produtos encontrados — selecione na lista abaixo ou refine o código.');
      }
    } catch (err) {
      setError(getApiErrorMessage(err, 'Erro ao buscar produto pelo código.'));
    } finally {
      setBipando(false);
    }
  };

  const montarPayload = () => ({
    estabelecimentoOrigemId: Number(origemId),
    estabelecimentoDestinoId: Number(destinoId),
    localEstoqueOrigemId: Number(localOrigemId) || 0,
    localEstoqueDestinoId: Number(localDestinoId) || 0,
    observacao: observacao.trim() || null,
    itens: itens
      .filter((i) => i.produto?.id)
      .map((i) => ({
        produtoId: Number(i.produto.id),
        qtdSolicitada: parseDecimal(i.qtdSolicitada),
        custoUnitario: i.custoUnitario !== '' ? parseDecimal(i.custoUnitario) : null,
      })),
  });

  const validarForm = () => {
    if (!origemId || !destinoId || !localOrigemId || !localDestinoId) {
      setError('Selecione filial e local de origem e de destino.');
      return false;
    }
    if (Number(origemId) === Number(destinoId) && Number(localOrigemId) === Number(localDestinoId)) {
      setError('Na mesma filial, escolha locais diferentes.');
      return false;
    }
    const payload = montarPayload();
    if (payload.itens.length === 0) {
      setError('Adicione ao menos um produto.');
      return false;
    }
    if (payload.itens.some((i) => i.qtdSolicitada <= 0)) {
      setError('Quantidades solicitadas devem ser maiores que zero.');
      return false;
    }
    return true;
  };

  const salvar = async () => {
    setError('');
    if (!validarForm()) return;
    setSaving(true);
    try {
      const payload = montarPayload();
      if (creating) {
        const { data } = await createTransferenciaEstoque(payload);
        navigate(`/stock-transfers/${data.id}`, { replace: true });
      } else {
        await updateTransferenciaEstoque(id, payload);
        await carregarDetalhe();
      }
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível salvar.'));
    } finally {
      setSaving(false);
    }
  };

  const abrirSeparar = () => {
    setDialogError('');
    setSepararItens(
      (detalhe?.itens || []).map((item) => ({
        itemId: item.id,
        produto: `${item.produtoCodigo} — ${item.produtoDescricao}`,
        qtdSolicitada: item.qtdSolicitada,
        saldoOrigem: Number(item.saldoOrigem ?? 0),
        qtdSeparada: String(item.qtdSeparada || item.qtdSolicitada),
      })),
    );
    setDlgSeparar(true);
  };

  const itensSeparacaoNegativos = (lista) => lista.filter(
    (i) => parseDecimal(i.qtdSeparada) > Number(i.saldoOrigem ?? 0),
  );

  const executarSeparar = async (forcarNegativo = false) => {
    setProcessando(true);
    setDialogError('');
    try {
      if (!forcarNegativo && permitirEstoqueNegativo) {
        const negativos = itensSeparacaoNegativos(separarItens);
        if (negativos.length > 0) {
          setDlgConfirmNegativo({
            action: 'separar',
            itens: negativos,
          });
          return;
        }
      }

      await separarTransferenciaEstoque(id, {
        bloquearNegativo: !forcarNegativo,
        itens: separarItens.map((i) => ({
          itemId: i.itemId,
          qtdSeparada: parseDecimal(i.qtdSeparada),
        })),
      });
      setDlgSeparar(false);
      setDlgConfirmNegativo(null);
      await carregarDetalhe();
    } catch (err) {
      setDialogError(getApiErrorMessage(err, 'Falha ao separar.'));
    } finally {
      setProcessando(false);
    }
  };

  const confirmarEstoqueNegativo = async () => {
    const action = dlgConfirmNegativo?.action;
    setDlgConfirmNegativo(null);
    if (action === 'separar') {
      await executarSeparar(true);
    }
  };

  const abrirConcluir = () => {
    setDialogError('');
    setConcluirItens(
      (detalhe?.itens || []).map((item) => ({
        itemId: item.id,
        produto: `${item.produtoCodigo} — ${item.produtoDescricao}`,
        qtdSeparada: item.qtdSeparada,
        qtdRecebida: String(item.qtdRecebida || item.qtdSeparada),
        motivoDivergencia: item.motivoDivergencia || '',
      })),
    );
    setDlgConcluir(true);
  };

  const executarConcluir = async () => {
    for (const item of concluirItens) {
      const recebida = parseDecimal(item.qtdRecebida);
      const separada = Number(item.qtdSeparada);
      if (recebida !== separada && !String(item.motivoDivergencia || '').trim()) {
        setDialogError(`Informe o motivo da divergência para: ${item.produto}`);
        return;
      }
    }
    setProcessando(true);
    setDialogError('');
    try {
      await concluirTransferenciaEstoque(id, {
        itens: concluirItens.map((i) => ({
          itemId: i.itemId,
          qtdRecebida: parseDecimal(i.qtdRecebida),
          motivoDivergencia: i.motivoDivergencia?.trim() || null,
        })),
      });
      setDlgConcluir(false);
      await carregarDetalhe();
    } catch (err) {
      setDialogError(getApiErrorMessage(err, 'Falha ao concluir.'));
    } finally {
      setProcessando(false);
    }
  };

  const executarCancelar = async () => {
    if (!motivoCancelamento.trim()) {
      setDialogError('Informe o motivo do cancelamento.');
      return;
    }
    setProcessando(true);
    setDialogError('');
    try {
      await cancelarTransferenciaEstoque(id, {
        motivoCancelamento: motivoCancelamento.trim(),
      });
      setDlgCancelar(false);
      setMotivoCancelamento('');
      await carregarDetalhe();
    } catch (err) {
      setDialogError(getApiErrorMessage(err, 'Falha ao cancelar.'));
    } finally {
      setProcessando(false);
    }
  };

  const baixarRomaneioPdf = async () => {
    if (!id || creating) return;
    setBaixandoPdf(true);
    setError('');
    try {
      const { data } = await getTransferenciaRomaneioPdf(id);
      const blob = data instanceof Blob
        ? data
        : new Blob([data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const nome = detalhe?.numero ? `romaneio-${detalhe.numero}.pdf` : `romaneio-${id}.pdf`;
      const a = document.createElement('a');
      a.href = url;
      a.download = nome.replace(/\//g, '-');
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível gerar o PDF do romaneio.'));
    } finally {
      setBaixandoPdf(false);
    }
  };

  const titulo = creating
    ? 'Nova transferência'
    : detalhe?.numero
      ? `Romaneio ${detalhe.numero}`
      : 'Transferência';

  const st = statusTransferencia(statusAtual ?? 'Criado');

  if (loading) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: 'auto' }}>
      <PageHeader
        title={titulo}
        subtitle={
          creating
            ? 'Planeje a transferência entre filiais.'
            : `Status: ${st.label}`
        }
        actions={
          <Stack direction="row" spacing={1}>
            {!creating && podeTransferencia(permissions, 'consultar') && (
              <Button
                variant="outlined"
                startIcon={<PictureAsPdfIcon />}
                onClick={baixarRomaneioPdf}
                disabled={baixandoPdf}
              >
                {baixandoPdf ? 'Gerando PDF…' : 'PDF do romaneio'}
              </Button>
            )}
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/stock-transfers')}>
              Voltar
            </Button>
          </Stack>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {!creating && detalhe && (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">Origem</Typography>
                <Typography>
                  {detalhe.estabelecimentoOrigemNome}
                  {detalhe.localEstoqueOrigemNome ? ` · ${detalhe.localEstoqueOrigemNome}` : ''}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">Destino</Typography>
                <Typography>
                  {detalhe.estabelecimentoDestinoNome}
                  {detalhe.localEstoqueDestinoNome ? ` · ${detalhe.localEstoqueDestinoNome}` : ''}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="text.secondary">Criado em</Typography>
                <Typography>{formatDateTimeLocale(detalhe.criadoEm)}</Typography>
              </Grid>
              {detalhe.separadoEm && (
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="text.secondary">Separado em</Typography>
                  <Typography>{formatDateTimeLocale(detalhe.separadoEm)}</Typography>
                </Grid>
              )}
              {detalhe.concluidoEm && (
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="text.secondary">Concluído em</Typography>
                  <Typography>{formatDateTimeLocale(detalhe.concluidoEm)}</Typography>
                </Grid>
              )}
              {detalhe.motivoCancelamento && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Motivo cancelamento</Typography>
                  <Typography>{detalhe.motivoCancelamento}</Typography>
                </Grid>
              )}
            </Grid>

            {!creating && (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
                {statusNome === 'Criado' && podeTransferencia(permissions, 'separar') && (
                  <Button variant="contained" color="warning" onClick={abrirSeparar}>
                    Separar (baixa origem)
                  </Button>
                )}
                {statusNome === 'Separado' && podeTransferencia(permissions, 'concluir') && (
                  <Button variant="contained" color="success" onClick={abrirConcluir}>
                    Concluir (entrada destino)
                  </Button>
                )}
                {(statusNome === 'Criado' || statusNome === 'Separado')
                  && podeTransferencia(permissions, 'cancelar') && (
                  <Button variant="outlined" color="error" onClick={() => { setDialogError(''); setDlgCancelar(true); }}>
                    Cancelar romaneio
                  </Button>
                )}
              </Stack>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent>
          <Stack spacing={2}>
            {editavel && (
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    label="Filial origem"
                    value={origemId}
                    onChange={(e) => setOrigemId(e.target.value)}
                    fullWidth
                    required
                  >
                    {filiais.map((f) => (
                      <MenuItem key={f.id} value={String(f.id)}>{f.nome}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    label="Filial destino"
                    value={destinoId}
                    onChange={(e) => setDestinoId(e.target.value)}
                    fullWidth
                    required
                  >
                    {destinosDisponiveis.map((f) => (
                      <MenuItem key={f.id} value={String(f.id)}>{f.nome}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    label="Local origem"
                    value={localOrigemId}
                    onChange={(e) => setLocalOrigemId(e.target.value)}
                    fullWidth
                    required
                    disabled={!origemId}
                  >
                    {locaisOrigem.map((l) => (
                      <MenuItem key={l.id} value={String(l.id)}>{l.nome}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    label="Local destino"
                    value={localDestinoId}
                    onChange={(e) => setLocalDestinoId(e.target.value)}
                    fullWidth
                    required
                    disabled={!destinoId}
                    helperText="Pode ser a mesma filial, desde que o local seja outro. O romaneio sai neste documento."
                  >
                    {locaisDestino.map((l) => (
                      <MenuItem key={l.id} value={String(l.id)}>{l.nome}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Observação"
                    value={observacao}
                    onChange={(e) => setObservacao(e.target.value)}
                    fullWidth
                    multiline
                    minRows={2}
                  />
                </Grid>
              </Grid>
            )}

            {!editavel && detalhe?.observacao && (
              <Box>
                <Typography variant="body2" color="text.secondary">Observação</Typography>
                <Typography>{detalhe.observacao}</Typography>
              </Box>
            )}

            <Typography variant="h6">Itens</Typography>

            {editavel ? (
              <Stack spacing={2}>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'action.hover' }}>
                  <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                    Leitor de barras
                  </Typography>
                  <TextField
                    inputRef={bipInputRef}
                    size="small"
                    fullWidth
                    autoFocus
                    placeholder={PRODUCT_BARCODE_HINT}
                    value={codigoBip}
                    onChange={(e) => setCodigoBip(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        void onEnterCodigoBip();
                      }
                    }}
                    disabled={bipando || !origemId}
                    helperText={
                      !origemId
                        ? 'Selecione a origem para habilitar o bip.'
                        : 'Código, EAN ou código de caixa/variação — Enter adiciona à lista.'
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <QrCodeScannerIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Paper>

                {itens.map((item, index) => (
                  <Grid container spacing={1} key={item.key} alignItems="center">
                    <Grid item xs={12} md={6}>
                      <Autocomplete
                        options={produtoOptions}
                        getOptionLabel={(opt) =>
                          opt ? `${opt.codigo || ''} — ${opt.descricao || ''}`.trim() : ''
                        }
                        isOptionEqualToValue={(a, b) => Number(a?.id) === Number(b?.id)}
                        value={item.produto}
                        onOpen={() => buscarProdutos('')}
                        onInputChange={(_, value) => buscarProdutos(value)}
                        onChange={(_, produto) => {
                          setItens((prev) => {
                            const next = [...prev];
                            next[index] = { ...next[index], produto };
                            return next;
                          });
                        }}
                        renderInput={(params) => (
                          <TextField {...params} label="Produto" required />
                        )}
                      />
                    </Grid>
                    <Grid item xs={6} md={2}>
                      <TextField
                        label="Qtd solicitada"
                        value={item.qtdSolicitada}
                        onChange={(e) => {
                          const val = e.target.value;
                          setItens((prev) => {
                            const next = [...prev];
                            next[index] = { ...next[index], qtdSolicitada: val };
                            return next;
                          });
                        }}
                        fullWidth
                        inputMode="decimal"
                      />
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <TextField
                        label="Custo unit. (opcional)"
                        value={item.custoUnitario}
                        onChange={(e) => {
                          const val = e.target.value;
                          setItens((prev) => {
                            const next = [...prev];
                            next[index] = { ...next[index], custoUnitario: val };
                            return next;
                          });
                        }}
                        fullWidth
                        inputMode="decimal"
                      />
                    </Grid>
                    <Grid item xs={12} md={1}>
                      <IconButton
                        color="error"
                        disabled={itens.length <= 1}
                        onClick={() => setItens((prev) => prev.filter((_, i) => i !== index))}
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                ))}
                <Button
                  startIcon={<AddIcon />}
                  onClick={() => setItens((prev) => [...prev, emptyItem()])}
                >
                  Adicionar item
                </Button>
              </Stack>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Produto</TableCell>
                    <TableCell align="right">Solicitada</TableCell>
                    <TableCell align="right">Separada</TableCell>
                    <TableCell align="right">Recebida</TableCell>
                    <TableCell>Divergência</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(detalhe?.itens || []).map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        {item.produtoCodigo} — {item.produtoDescricao}
                      </TableCell>
                      <TableCell align="right">{item.qtdSolicitada}</TableCell>
                      <TableCell align="right">{item.qtdSeparada}</TableCell>
                      <TableCell align="right">{item.qtdRecebida}</TableCell>
                      <TableCell>{item.motivoDivergencia || '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}

            {editavel && (
              <Stack direction="row" justifyContent="flex-end" spacing={1}>
                <Button onClick={() => navigate('/stock-transfers')}>Cancelar</Button>
                <Button
                  variant="contained"
                  onClick={salvar}
                  disabled={saving || (creating ? !podeTransferencia(permissions, 'criar') : !podeTransferencia(permissions, 'editar'))}
                >
                  {saving ? 'Salvando…' : creating ? 'Criar romaneio' : 'Salvar alterações'}
                </Button>
              </Stack>
            )}
          </Stack>
        </CardContent>
      </Card>

      <Dialog open={dlgSeparar} onClose={() => !processando && setDlgSeparar(false)} maxWidth="md" fullWidth>
        <DialogTitle>Separar — conferência na origem</DialogTitle>
        <DialogContent>
          {dialogError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDialogError('')}>
              {dialogError}
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Confirme as quantidades separadas. Será feita a baixa de estoque na filial de origem.
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Produto</TableCell>
                <TableCell align="right">Saldo origem</TableCell>
                <TableCell align="right">Solicitada</TableCell>
                <TableCell align="right">Separada</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {separarItens.map((item, index) => {
                const qtd = parseDecimal(item.qtdSeparada);
                const saldo = Number(item.saldoOrigem ?? 0);
                const ficaraNegativo = qtd > saldo;
                return (
                <TableRow key={item.itemId} sx={ficaraNegativo ? { bgcolor: 'warning.light' } : undefined}>
                  <TableCell>{item.produto}</TableCell>
                  <TableCell align="right">{saldo}</TableCell>
                  <TableCell align="right">{item.qtdSolicitada}</TableCell>
                  <TableCell align="right">
                    <TextField
                      size="small"
                      value={item.qtdSeparada}
                      onChange={(e) => {
                        const val = e.target.value;
                        setSepararItens((prev) => {
                          const next = [...prev];
                          next[index] = { ...next[index], qtdSeparada: val };
                          return next;
                        });
                      }}
                      inputMode="decimal"
                      sx={{ maxWidth: 120 }}
                    />
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlgSeparar(false)} disabled={processando}>Voltar</Button>
          <Button variant="contained" color="warning" onClick={() => executarSeparar(false)} disabled={processando}>
            Confirmar separação
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(dlgConfirmNegativo)}
        onClose={() => !processando && setDlgConfirmNegativo(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Estoque ficará negativo</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            A separação deixará o saldo negativo na filial de origem para os itens abaixo.
            Deseja continuar mesmo assim?
          </Typography>
          <Stack spacing={1}>
            {(dlgConfirmNegativo?.itens || []).map((item) => (
              <Typography key={item.itemId} variant="body2">
                {item.produto} — saldo {Number(item.saldoOrigem ?? 0)}, separar {parseDecimal(item.qtdSeparada)}
              </Typography>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlgConfirmNegativo(null)} disabled={processando}>Não</Button>
          <Button variant="contained" color="warning" onClick={confirmarEstoqueNegativo} disabled={processando}>
            Sim, continuar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dlgConcluir} onClose={() => !processando && setDlgConcluir(false)} maxWidth="md" fullWidth>
        <DialogTitle>Concluir — recebimento no destino</DialogTitle>
        <DialogContent>
          {dialogError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDialogError('')}>
              {dialogError}
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Informe as quantidades recebidas. Motivo obrigatório se diferir da separada.
          </Typography>
          <Stack spacing={2}>
            {concluirItens.map((item, index) => {
              const diverge = parseDecimal(item.qtdRecebida) !== Number(item.qtdSeparada);
              return (
                <Card key={item.itemId} variant="outlined">
                  <CardContent>
                    <Typography fontWeight={600} gutterBottom>{item.produto}</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6} md={3}>
                        <Typography variant="caption" color="text.secondary">Separada</Typography>
                        <Typography>{item.qtdSeparada}</Typography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <TextField
                          label="Recebida"
                          size="small"
                          fullWidth
                          value={item.qtdRecebida}
                          onChange={(e) => {
                            const val = e.target.value;
                            setConcluirItens((prev) => {
                              const next = [...prev];
                              next[index] = { ...next[index], qtdRecebida: val };
                              return next;
                            });
                          }}
                          inputMode="decimal"
                        />
                      </Grid>
                      {diverge && (
                        <Grid item xs={12} md={6}>
                          <TextField
                            label="Motivo da divergência"
                            size="small"
                            fullWidth
                            required
                            value={item.motivoDivergencia}
                            onChange={(e) => {
                              const val = e.target.value;
                              setConcluirItens((prev) => {
                                const next = [...prev];
                                next[index] = { ...next[index], motivoDivergencia: val };
                                return next;
                              });
                            }}
                          />
                        </Grid>
                      )}
                    </Grid>
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlgConcluir(false)} disabled={processando}>Voltar</Button>
          <Button variant="contained" color="success" onClick={executarConcluir} disabled={processando}>
            Confirmar recebimento
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dlgCancelar} onClose={() => !processando && setDlgCancelar(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Cancelar romaneio</DialogTitle>
        <DialogContent>
          {dialogError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDialogError('')}>
              {dialogError}
            </Alert>
          )}
          <TextField
            label="Motivo do cancelamento"
            value={motivoCancelamento}
            onChange={(e) => setMotivoCancelamento(e.target.value)}
            fullWidth
            multiline
            minRows={3}
            required
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlgCancelar(false)} disabled={processando}>Voltar</Button>
          <Button variant="contained" color="error" onClick={executarCancelar} disabled={processando}>
            Confirmar cancelamento
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
